import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  forwardRef,
  inject,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { registerApiFieldTarget } from '../../../service/api-field-errors.service';

export interface SelectOption {
  label: string;
  value: unknown;
  subtext?: string;
  imageUrl?: string;
  /** Destructive choices render red in the list and on the trigger. */
  tone?: 'default' | 'danger';
}

export interface SelectOptionGroup {
  heading: string;
  options: SelectOption[];
}

/** One of the lists a select can switch between (Lender / Provider). */
export interface SelectSource {
  value: string;
  label: string;
}

type SelectSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './select.html',
  styles: [`
    :host {
      display: contents;
    }
    .app-select-root {
      overflow: visible !important;
      position: relative;
    }
    /* The × only gets the room the selected text leaves over; once it can't fit
       in full it is hidden outright rather than shown half-clipped. */
    .app-select-clear-hidden {
      visibility: hidden;
      pointer-events: none;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  /** Lets a rejected save's field error find, mark and scroll to this field. */
  private readonly apiFieldTarget = registerApiFieldTarget(() => [this.currentControl], () => this.label);
  private standaloneValue: unknown = '';
  private cvaDisabled = false;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private _options: Array<string | SelectOption> = [];
  private _upward = false;

  @Input() form?: FormGroup;
  @Input() controlName = '';
  @Input() control?: FormControl | AbstractControl | null;
  @Input() label = '';
  @Input() labelClass = 'mb-1.5 block text-sm font-medium text-slate900';
  

  @Input() placeholder = 'Select an option';
  @Input() set options(value: readonly (string | SelectOption)[] | null | undefined) {
    this._options = Array.isArray(value) ? [...value] : [];
  }
  get options(): Array<string | SelectOption> {
    return this._options;
  }
  @Input() optionGroups: SelectOptionGroup[] = [];
  @Input() error = '';
  @Input() emptyMessage = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() size: SelectSize = 'lg';
  @Input() upward = false;
  @Input() position: 'top' | 'bottom' | 'auto' = 'auto';
  @Input() multiple = false;
  @Input() disableWhenEmpty = true;
  @Input() searchable = false;
  /**
   * On by default: a selection can be cleared with the × or by picking the
   * selected option again. Pass `false` to keep a value from being emptied.
   */
  @Input() clearable = true;
  @Input() showCheckbox = false;
  @Input() fitDropdownToContent = false;
  /** Turns the trigger into an editable text input: pick an option, search, or type free text to save as-is. */
  @Input() allowCustomValue = false;
  /**
   * The typed query is also reported to the consumer, so it can fetch the
   * options matching it — which is what lets a select reach past the page of
   * them it was given. The list is still filtered here on top of whatever comes
   * back; see `filteredOptions`.
   */
  @Input() remoteSearch = false;

  /** What was typed in the dropdown's search box, debounced. `remoteSearch` only. */
  @Output() searchQueryChange = new EventEmitter<string>();
  /**
   * Lists the options can come from, shown as a switch at the top of the menu. The consumer
   * swaps `options` for the picked one on `activeSourceChange`; the menu stays open.
   */
  @Input() sources: SelectSource[] = [];
  @Input() activeSource = '';
  @Output() activeSourceChange = new EventEmitter<string>();

  selectSource(source: string, event: Event): void {
    event.stopPropagation();
    if (source === this.activeSource) return;
    this.searchQuery = '';
    this.activeSourceChange.emit(source);
  }

  isOpen = false;
  searchQuery = '';
  /** Whether the custom-value input has been typed in since the menu opened. */
  private customQueryEdited = false;

  /**
   * Optional `#optionIcon` template, rendered in place of `imageUrl` for both the
   * trigger and every option row. Use it when the leading visual is richer than an
   * image — an inline SVG, a coloured tile — and receives the option as `$implicit`.
   */
  @ContentChild('optionIcon') optionIconTemplate?: TemplateRef<{ $implicit: SelectOption }>;

  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLElement>;
  @ViewChild('dropdownMenu') private dropdownMenu?: ElementRef<HTMLDivElement>;

  /**
   * The trigger lays the selected text out at full width first, and the × shrinks
   * into whatever is left. This watches the × and hides it whole (see
   * `.app-select-clear-hidden`) once it has been squeezed below its own size.
   * Visibility doesn't affect layout, so toggling it can't feed back into a resize.
   */
  @ViewChild('clearButton') private set clearButtonRef(ref: ElementRef<HTMLElement> | undefined) {
    this.observeClearButton(ref?.nativeElement);
  }
  private resizeObserver?: ResizeObserver;
  private observedClearButton?: HTMLElement;

  private observeClearButton(el: HTMLElement | undefined): void {
    if (el === this.observedClearButton) {
      return;
    }
    this.resizeObserver?.disconnect();
    this.observedClearButton = el;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      el.classList.toggle('app-select-clear-hidden', el.scrollWidth > el.clientWidth + 1);
    });
    this.resizeObserver.observe(el);
  }

  ngOnDestroy(): void {
    // Set first: closing on the way out must not ask the consumer to refetch a
    // list for a select that is no longer on screen.
    this.isDestroyed = true;
    clearTimeout(this.searchDebounce);
    this.resizeObserver?.disconnect();
    this.closeMenu();
  }

  private searchDebounce?: ReturnType<typeof setTimeout>;
  /** The last query the consumer was given, so the same one is not asked twice. */
  private lastRemoteQuery = '';
  private isDestroyed = false;

  /**
   * Typing in the dropdown's search box. A remote search is held back briefly so
   * a word costs one request rather than one per keystroke.
   */
  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.emitRemoteSearch(value.trim());
  }

  /**
   * Asks the consumer for the options matching a query. Debounced unless the
   * query is being reset, which happens on close and should land at once.
   */
  private emitRemoteSearch(query: string, immediate = false): void {
    if (!this.remoteSearch || this.isDestroyed) {
      return;
    }

    clearTimeout(this.searchDebounce);

    const emit = (): void => {
      if (query === this.lastRemoteQuery) {
        return;
      }

      this.lastRemoteQuery = query;
      this.searchQueryChange.emit(query);
    };

    if (immediate) {
      emit();
      return;
    }

    this.searchDebounce = setTimeout(emit, 300);
  }
 
  get normalizedOptions(): SelectOption[] {
    return this.options.map((option) =>
      typeof option === 'string' ? { label: option, value: option } : option,
    );
  }
 
  get filteredOptions(): SelectOption[] {
    // Note that a remote search is filtered here as well. The options it hands
    // back have already been narrowed, so this is normally a no-op — but an
    // endpoint that quietly ignores the query would otherwise leave the box
    // typing into a list that never changes.
    // A custom-value trigger opens pre-filled with the current value; until that
    // text is edited it is not a search, so the whole list stays on offer.
    if (
      (!this.searchable && !this.allowCustomValue) ||
      !this.searchQuery.trim() ||
      (this.allowCustomValue && !this.customQueryEdited)
    ) {
      return this.normalizedOptions;
    }
    const q = this.searchQuery.trim().toLowerCase();
    return this.normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }

  /** Text shown in the custom-value trigger input when the dropdown is closed. */
  get displayText(): string {
    const label = this.selectedLabel;
    if (label) {
      return label;
    }
    const raw = this.selectedValue;
    return typeof raw === 'string' ? raw : '';
  }

  /** Whether to show a "Use '<typed text>'" row for saving a value that isn't in the option list. */
  get showUseTypedOption(): boolean {
    if (!this.allowCustomValue || !this.isOpen) {
      return false;
    }
    const typed = this.searchQuery.trim();
    if (!typed) {
      return false;
    }
    return !this.normalizedOptions.some((o) => o.label.toLowerCase() === typed.toLowerCase());
  }
 
  get currentControl(): AbstractControl | null {
    if (this.control) {
      return this.control;
    }

    if (!this.form || !this.controlName) {
      return null;
    }

    return this.form.get(this.controlName);
  }
 
  get selectedValue(): unknown {
    return this.currentControl ? this.currentControl.value : this.standaloneValue;
  }
 
  get selectedValues(): unknown[] {
    if (!this.multiple) return [];
    const value = this.selectedValue;
    return Array.isArray(value) ? value : [];
  }

  get selectedLabel(): string {
    if (this.multiple) {
      const selected = this.normalizedOptions
        .filter((option) => this.selectedValues.includes(option.value))
        .map((option) => option.label);
      return selected.join(', ');
    }
    const selected = this.normalizedOptions.find((option) => option.value === this.selectedValue);
    return selected?.label ?? '';
  }

  get selectedOption(): SelectOption | undefined {
    if (this.multiple) return undefined;
    return this.normalizedOptions.find((option) => option.value === this.selectedValue);
  }

  optionToneClass(option: SelectOption, isSelected: boolean): string {
    if (option.tone === 'danger') {
      return isSelected ? 'bg-red50 text-red700' : 'text-red600';
    }

    return isSelected ? 'bg-indigo50 text-indigo600' : 'text-slate900';
  }

  get triggerToneClass(): string {
    // Placeholder text and an unusable trigger are both greyed out; a picked option
    // keeps its own tone. The trigger's text colour has to be set on this span —
    // the wrapper's colour is only inherited, so it never reaches the label.
    if (this.isDisabled || this.isEmptyDisabled || !this.selectedLabel) {
      return 'text-slate400';
    }

    return this.selectedOption?.tone === 'danger' ? 'text-red600' : 'text-slate800';
  }
 
  get shouldShowError(): boolean {
    const control = this.currentControl;
    if (!control) {
      return !!this.error;
    }

    return !!this.error || (control.invalid && (control.dirty || control.touched));
  }
 
  get fallbackError(): string {
    const control = this.currentControl;
    if (!control?.errors) {
      return '';
    }

    const serverError = this.resolveTextError(control.errors['server'] ?? control.errors['api']);
    if (serverError) {
      return serverError;
    }

    if (control.errors['required']) {
      return `${this.label || 'This field'} is required.`;
    }

    if (control.errors['singleLifeOnly']) {
      return `${this.label || 'This field'} must be a single life (First or Second only).`;
    }

    if (control.errors['oneOf']) {
      return `${this.label || 'This field'} must be one of: ${control.errors['oneOf'].allowed.join(', ')}.`;
    }

    return 'Invalid value.';
  }
 
  get hasOptions(): boolean {
    return this.filteredOptions.length > 0;
  }
 
  get isEmptyDisabled(): boolean {
    return this.disableWhenEmpty && !this.hasOptions && !this.allowCustomValue;
  }
 
  get resolvedEmptyMessage(): string {
    const customMessage = this.emptyMessage.trim();

    if (customMessage) {
      return customMessage;
    }

    const fieldName = this.label.trim() || 'options';
    return `No ${fieldName} available`;
  }
 
  dropdownStyle: Record<string, string> = {};

  private readonly scrollListener = (): void => {
    if (this.isOpen) {
      this.updatePosition();
    }
  };

  private readonly outsideClickListener = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Node) || !this.hostElement.nativeElement.contains(target)) {
      this.closeMenuWithCommit();
      this.cdr.detectChanges();
    }
  };

  private bindScrollListener(): void {
    document.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    window.addEventListener('resize', this.scrollListener, { passive: true });
    // Capture phase so a child elsewhere calling stopPropagation can't keep the menu open.
    document.addEventListener('pointerdown', this.outsideClickListener, true);
  }

  private unbindScrollListener(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('resize', this.scrollListener);
    document.removeEventListener('pointerdown', this.outsideClickListener, true);
  }

  updatePosition(): void {
    if (!this.triggerButton) {
      return;
    }
    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    
    let targetPosition: 'top' | 'bottom' = 'bottom';
    
    if (this.upward || this.position === 'top') {
      targetPosition = 'top';
    } else if (this.position === 'bottom') {
      targetPosition = 'bottom';
    } else {
      // Auto collision detection: open upward if there's not enough room below and there's more room above
      const dropdownHeight = this.dropdownMenu?.nativeElement?.offsetHeight || 280;
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const spaceAbove = rect.top - 4;
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        targetPosition = 'top';
      } else {
        targetPosition = 'bottom';
      }
    }
    
    const widthStyle: Record<string, string> = this.fitDropdownToContent
      ? {
          minWidth: `${rect.width}px`,
          width: 'max-content',
          // The whole screen, not just what is right of the trigger: a menu without room on
          // the right is moved left below rather than squeezed or cut off.
          maxWidth: `${Math.max(rect.width, window.innerWidth - 2 * SelectComponent.viewportMargin)}px`,
        }
      : {
          minWidth: `${rect.width}px`,
          width: `${rect.width}px`,
          maxWidth: `${rect.width}px`,
        };

    if (targetPosition === 'top') {
      this.dropdownStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        ...widthStyle,
        bottom: `${window.innerHeight - rect.top + 4}px`,
        top: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    } else {
      this.dropdownStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        ...widthStyle,
        top: `${rect.bottom + 4}px`,
        bottom: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    }

    this.cdr.detectChanges();
    this.keepMenuOnScreen(rect.left);
  }

  /** Space kept between an open menu and the screen's edges. */
  private static readonly viewportMargin = 8;

  /** Opened beside the trigger, the menu may run past the right edge: slide it left until it fits. */
  private keepMenuOnScreen(triggerLeft: number): void {
    const menuWidth = this.dropdownMenu?.nativeElement?.offsetWidth;
    if (!menuWidth) return;
    const margin = SelectComponent.viewportMargin;
    const left = Math.max(margin, Math.min(triggerLeft, window.innerWidth - menuWidth - margin));
    if (left === triggerLeft) return;
    this.dropdownStyle = { ...this.dropdownStyle, left: `${left}px` };
    this.cdr.detectChanges();
  }

  toggle(): void {
    if (this.isDisabled) {
      return;
    }

    if (this.isOpen) {
      this.closeMenuWithCommit();
      return;
    }

    this.openMenu();
  }

  /** Opens the dropdown. Public so the custom-value input can open it on focus. */
  openMenu(): void {
    if (this.isDisabled || this.isOpen) {
      return;
    }

    // Initialize with fixed positioning and hide it to avoid causing a layout flash or scrollbars
    this.dropdownStyle = {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '99999',
    };
    this.isOpen = true;
    this.searchQuery = this.allowCustomValue ? this.displayText : '';
    this.customQueryEdited = false;

    // Synchronously trigger render so the dropdown is added to the DOM and can be measured immediately
    this.cdr.detectChanges();

    this.updatePosition();
    this.bindScrollListener();
  }

  onCustomInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.customQueryEdited = true;
    if (!this.isOpen) {
      this.openMenu();
    }
  }

  onCustomEnter(event: Event): void {
    event.preventDefault();
    this.closeMenuWithCommit();
  }

  onCustomEscape(): void {
    this.searchQuery = this.displayText;
    this.closeMenu();
  }

  useTypedValue(): void {
    const typed = this.searchQuery.trim();
    if (!typed) {
      return;
    }
    this.setSingleValue(typed);
    this.closeMenu();
  }

  private shouldOpenUpward(): boolean {
    const element = this.hostElement.nativeElement;
    const rect = element.getBoundingClientRect();
    const dropdownHeight = 250;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    return spaceBelow < dropdownHeight;
  }
 
  clearValue(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isDisabled) {
      return;
    }

    // Otherwise the text still sitting in the custom-value input is committed
    // straight back as the value when the menu closes.
    if (this.allowCustomValue) {
      this.searchQuery = '';
    }
    
    const control = this.currentControl;
    if (!control) {
      this.updateValue(this.multiple ? [] : '');
      return;
    }

    control.setValue(this.multiple ? [] : '');
    control.markAsDirty();
    control.markAsTouched();
    this.updateValue(this.multiple ? [] : '');
  }

  selectOption(option: SelectOption): void {
    if (this.isEmptyDisabled) {
      return;
    }

    // Picking the option that is already selected clears it, the same as the ×.
    if (!this.multiple && this.clearable && this.isOptionSelected(option)) {
      this.setSingleValue('');
      this.closeMenu();
      return;
    }

    const control = this.currentControl;
    if (!control) {
      this.updateValue(option.value);
      this.closeMenu();
      return;
    }

    if (this.multiple) {
      const currentValues = this.selectedValues;
      const exists = currentValues.some((value) => value === option.value);
      const nextValues = exists
        ? currentValues.filter((value) => value !== option.value)
        : [...currentValues, option.value];
      control.setValue(nextValues);
      control.markAsDirty();
      control.markAsTouched();
      this.updateValue(nextValues);
      return;
    }

    this.setSingleValue(option.value);
    this.closeMenu();
  }

  isOptionSelected(option: SelectOption): boolean {
    if (this.multiple) {
      return this.selectedValues.includes(option.value);
    }
    return this.selectedValue === option.value;
  }
 
  get triggerClassMap(): Record<string, boolean> {
    return {
      'h-[46px]': this.size === 'lg',
      'h-[40px]': this.size === 'md',
      'h-9': this.size === 'sm',
      'border-red500': this.shouldShowError,
      'border-slate300': !this.shouldShowError,
      'cursor-not-allowed bg-slate100 text-slate500': this.isDisabled || this.isEmptyDisabled,
      'cursor-pointer bg-slate50': !this.isDisabled && !this.isEmptyDisabled,
    };
  }
 
  get triggerTextSizeClass(): string {
    return this.size === 'sm' ? 'text-[13px]' : 'text-sm';
  }
 
  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled || !!this.currentControl?.disabled;
  }
 
  writeValue(value: unknown): void {
    this.standaloneValue = value;
  }
 
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
 
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
 
  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }
 
  private updateValue(value: unknown): void {
    this.standaloneValue = value;
    this.onChange(value);
    this.onTouched();
  }

  private closeMenu(): void {
    this.isOpen = false;
    this.searchQuery = this.allowCustomValue ? this.searchQuery : '';
    this.unbindScrollListener();
    // A remote list is still holding the last search's results, so it is put back
    // to its full self on the way out — reopening the menu shows the list it
    // started with rather than what was last typed into it.
    this.emitRemoteSearch('', true);
  }

  private closeMenuWithCommit(): void {
    if (this.allowCustomValue) {
      this.commitTypedValue();
    }
    this.closeMenu();
  }

  /** On close, saves the typed text as-is unless it matches an option's label. */
  private commitTypedValue(): void {
    const typed = this.searchQuery.trim();
    const matched = this.normalizedOptions.find(
      (o) => o.label.toLowerCase() === typed.toLowerCase(),
    );
    const nextValue = matched ? matched.value : typed || null;

    if (nextValue === this.selectedValue) {
      return;
    }

    this.setSingleValue(nextValue);
  }

  private setSingleValue(value: unknown): void {
    const control = this.currentControl;
    if (!control) {
      this.updateValue(value);
      return;
    }

    control.setValue(value);
    control.markAsDirty();
    control.markAsTouched();
    this.updateValue(value);
  }

  private resolveTextError(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
